import { Bounds } from './objectGeometry';

export type AlignEdge =
  | 'left'
  | 'center'
  | 'right'
  | 'top'
  | 'middle'
  | 'bottom';
export type Axis = 'horizontal' | 'vertical';

export interface Offset {
  x: number;
  y: number;
}

// How far `bounds` has to move so the given edge (or center line) lines up
// with the same edge of `target`.
export const getAlignOffset = (
  bounds: Bounds,
  target: Bounds,
  edge: AlignEdge,
): Offset => {
  switch (edge) {
    case 'left':
      return { x: target.left - bounds.left, y: 0 };
    case 'center':
      return {
        x: target.left + target.width / 2 - (bounds.left + bounds.width / 2),
        y: 0,
      };
    case 'right':
      return {
        x: target.left + target.width - (bounds.left + bounds.width),
        y: 0,
      };
    case 'top':
      return { x: 0, y: target.top - bounds.top };
    case 'middle':
      return {
        x: 0,
        y: target.top + target.height / 2 - (bounds.top + bounds.height / 2),
      };
    case 'bottom':
      return {
        x: 0,
        y: target.top + target.height - (bounds.top + bounds.height),
      };
  }
};

export const getUnionBounds = (boundsList: Bounds[]): Bounds => {
  const left = Math.min(...boundsList.map((bounds) => bounds.left));
  const top = Math.min(...boundsList.map((bounds) => bounds.top));
  const right = Math.max(...boundsList.map((b) => b.left + b.width));
  const bottom = Math.max(...boundsList.map((b) => b.top + b.height));
  return { left, top, width: right - left, height: bottom - top };
};

// Keeps the first and last object (along the axis) in place and spaces the
// others so the gaps between neighbours are equal. Returns one offset per
// input, in input order.
export const getDistributeOffsets = (
  boundsList: Bounds[],
  axis: Axis,
): Offset[] => {
  const start = axis === 'horizontal' ? 'left' : 'top';
  const size = axis === 'horizontal' ? 'width' : 'height';
  const order = boundsList
    .map((bounds, index) => ({ bounds, index }))
    .sort((a, b) => a.bounds[start] - b.bounds[start]);
  const first = order[0].bounds;
  const last = order[order.length - 1].bounds;
  const span = last[start] + last[size] - first[start];
  const occupied = order.reduce((sum, { bounds }) => sum + bounds[size], 0);
  const gap = (span - occupied) / Math.max(order.length - 1, 1);

  const offsets: Offset[] = boundsList.map(() => ({ x: 0, y: 0 }));
  let position = first[start];
  order.forEach(({ bounds, index }) => {
    const delta = position - bounds[start];
    offsets[index] =
      axis === 'horizontal' ? { x: delta, y: 0 } : { x: 0, y: delta };
    position += bounds[size] + gap;
  });
  return offsets;
};
